import { Box, Button, FormControlLabel, Grid2 as Grid, Switch } from "@mui/material"
import { GridColDef, GridRowId, GridRowSelectionModel } from "@mui/x-data-grid"
import { useEffect, useState } from "react"
import { CollectionEventEditor } from "../../components/CollectionEventEditor/CollectionEventEditor"
import { NexDrawer } from "../../components/Drawer/Drawer"
import { DataGridModal } from "../../components/Modals/DataGridModal"
import { MultipleFlightPlanSelector } from "../../components/MultipleFlightPlanSelector/MultipleFlightPlanSelector"
import { Label } from "../../components/Typography/Typography"
import { useMissions } from "../../hooks/useMissions"
import { useNexusMissionFlow } from "../../hooks/useNexusParams"
import { useToggle } from "../../hooks/useToggle"
import { FlightlineEvent } from "../../models/FlightlineEvent"
import { FlightplanType } from "../../store/types/FlightPlansTypes"
import { OptionsType } from "../../store/types/MissionsTypes"
import { MissionUtils } from "../../utils/MissionUtils"
import { useCollectionEventEditorStore } from "../../zstore/CollectionEventEditorStore"
import { useFlightlineEventStore } from "../../zstore/FlightlineEventStore"
import { FlightlineImport } from "./FlightlineImport"
import { FlightlineMatchingExpression } from "./FlightlineMatchingExpression"
import { MissionEditFlightlineEventsTable } from "./MissionEditFlightlineEventsTable"
import { useStyles } from "./styles"

export const FlightlineDetails = () => {
    const { classes } = useStyles()
    const missionFlow = useNexusMissionFlow()
    const { isNoFile, isFullFlight } = useMissions()

    const allFlightplans = useFlightlineEventStore((state) => state.allFlightplans)
    const selectedFlightplans = useFlightlineEventStore((state) => state.selectedFlightplans)
    const eventPlannedLines = useFlightlineEventStore((state) => state.eventPlannedLines)
    const flightlineEvents = useFlightlineEventStore((state) => state.flightlineEvents)
    const updateSelectedFlightplans = useFlightlineEventStore((state) => state.updateSelectedFlightplans)
    const updateFlightlineEvents = useFlightlineEventStore((state) => state.updateFlightlineEvents)
    const fetchEventPlannedLines = useFlightlineEventStore((state) => state.fetchEventPlannedLines)
    const isFetchingPlannedLines = useFlightlineEventStore((state) => state.isFetchingPlannedLines)

    // Saved separately first so the flightline events are only updated after user confirms flightplan changes
    // and only saved to state on close of flightplan modal selection
    const [userSelectedFlightPlanIds, setUserSelectedFlightPlanIds] = useState<GridRowSelectionModel>({
        type: "include",
        ids: new Set(),
    })
    // Load everything before we stop showing the loading bar
    const [optionsModalOpen, toggleOptionsModalOpen] = useToggle()
    const setEventCreator = useCollectionEventEditorStore((state) => state.setValuesFromFlightline)
    const isFlightlineCreatorDrawerOpen = useCollectionEventEditorStore((state) => state.isDrawerOpen)

    const [canSelectInactiveFlightplans, setCanSelectInactiveFlightplans] = useState(false)

    const isSubmitFlow = missionFlow === "submit"

    const columns: GridColDef<OptionsType[]>[] = [
        { field: "id", headerName: "Plan ID", type: "number", minWidth: 130, flex: 2 },
        { field: "flightplan_name", headerName: "Plan Name", minWidth: 190, flex: 5 },
        { field: "status", headerName: "Status", minWidth: 150, flex: 3 },
    ]

    useEffect(() => {
        const selectedSet: Set<GridRowId> = new Set()
        selectedFlightplans.forEach((plan) => {
            selectedSet.add(plan.flight_plan_id!)
        })

        setUserSelectedFlightPlanIds({ type: "include", ids: selectedSet })
    }, [])

    useEffect(() => {
        if (!isFetchingPlannedLines) {
            // update events to match the currently selected flight plans/planned lines
            const eventsWithUpdatedEventPlannedLines: FlightlineEvent[] = flightlineEvents.map((event) => {
                const eventPlannedLineIds = event.fe_planned_line_ids
                const matchingEventPlannedLines = eventPlannedLines.filter(
                    (pl) =>
                        eventPlannedLineIds.includes(pl.plannedLineId) ||
                        event.fe_name_from_original === pl.plannedLineName,
                )
                return {
                    ...event,
                    fe_planned_line_ids: matchingEventPlannedLines.map((epl) => epl.plannedLineId),
                }
            })

            updateFlightlineEvents(eventsWithUpdatedEventPlannedLines)
        }
    }, [isFetchingPlannedLines])

    const handleOptionsFlightplanUpdate = (model: GridRowSelectionModel) => {
        setUserSelectedFlightPlanIds(model)
    }

    const handleOptionsModalClose = async () => {
        // update frontend with new list of flightplans.
        const selectedFlightplans: FlightplanType[] = []
        allFlightplans.forEach((plan) => {
            if (plan.flight_plan_id != null && userSelectedFlightPlanIds.ids.has(plan.flight_plan_id)) {
                selectedFlightplans.push(plan)
            }
        })

        // Save selected flightplans to state
        updateSelectedFlightplans(selectedFlightplans)

        const selectedFlightplanIds: number[] = []
        selectedFlightplans.forEach((fp) => {
            if (fp.flight_plan_id) {
                selectedFlightplanIds.push(fp.flight_plan_id)
            }
        })
        // fetch the planned lines connected to the flight plans selected
        fetchEventPlannedLines(selectedFlightplanIds)

        toggleOptionsModalOpen()
    }

    const handleFlightLineCreator = (line?: FlightlineEvent) => {
        setEventCreator(line)
    }

    return (
        <Grid>
            <Label mb={2}>Lines Flown</Label>

            {/* Flight Plan Selector */}
            <Grid className={classes.gutterBottom__lg}>
                <MultipleFlightPlanSelector
                    selectedPlans={selectedFlightplans}
                    manageFlightPlans={() => toggleOptionsModalOpen()}
                />
            </Grid>

            {/* Table Regex Filters */}
            {isSubmitFlow && isFullFlight && <FlightlineMatchingExpression />}
            {isSubmitFlow && isNoFile && <FlightlineImport />}

            {/* Table */}
            <Box sx={{ pb: 2 }}>
                <MissionEditFlightlineEventsTable />
            </Box>

            {/* Add Additional Flightlines*/}
            {!(isSubmitFlow && isNoFile) && (
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={() => handleFlightLineCreator()}
                    sx={{ mb: 4 }}
                >
                    Add Additional Flightline
                </Button>
            )}

            {/* Modal */}
            <DataGridModal
                columns={columns}
                open={optionsModalOpen}
                title={"Manage Flight Plans"}
                rows={MissionUtils.transformFlightPlansToOptions(allFlightplans)}
                selected={userSelectedFlightPlanIds}
                onConfirm={handleOptionsModalClose}
                confirmText={"OK"}
                danger={false}
                onClose={handleOptionsModalClose}
                onSelectionChange={handleOptionsFlightplanUpdate}
                isRowSelectableFunct={(params) =>
                    canSelectInactiveFlightplans ||
                    params.row.status === "Active" ||
                    userSelectedFlightPlanIds.ids.has(params.row.id)
                }
                additionalActions={
                    <FormControlLabel
                        label="Unlock All Flightplans"
                        control={
                            <Switch
                                checked={canSelectInactiveFlightplans}
                                onChange={() => setCanSelectInactiveFlightplans(!canSelectInactiveFlightplans)}
                            />
                        }
                    />
                }
            >
                <></>
            </DataGridModal>
            {/* Drawer */}
            {isFlightlineCreatorDrawerOpen && (
                <NexDrawer
                    open={isFlightlineCreatorDrawerOpen}
                    title="Manage A Flight Line"
                    onClose={() => handleFlightLineCreator()}
                >
                    <CollectionEventEditor />
                </NexDrawer>
            )}
        </Grid>
    )
}
